import { useEffect, useMemo, useState } from "react";
import {
  currentUserEmail,
  forceRefreshTokens,
  performLogout,
  redirectToLogin,
  requireSession,
} from "../auth";
import { getAppUrls, isConfigured } from "../config";
import { LAUNCHER_APPS, type LauncherApp } from "../apps";
import { isSuperAdmin } from "../lib/role";
import { displayNameFromEmail, initialsFromEmail } from "../lib/user";

const AUTH_BOUNCE_COUNT_KEY = "oceany_app_auth_bounce_count";
const MAX_BOUNCES = 2;

export function Launcher() {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [superadmin, setSuperadmin] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isConfigured()) {
        if (!cancelled) {
          setAuthError(
            "Config OAuth mancante. Da web/ esegui: npm run app:env (richiede Terraform applicato), poi npm run app:dev.",
          );
          setChecking(false);
        }
        return;
      }

      let bounces = 0;
      try {
        bounces = Number(sessionStorage.getItem(AUTH_BOUNCE_COUNT_KEY) || "0");
      } catch {
        /* ignore */
      }

      const ok = await requireSession();
      if (cancelled) return;

      if (ok) {
        try {
          sessionStorage.removeItem(AUTH_BOUNCE_COUNT_KEY);
        } catch {
          /* ignore */
        }
        setSuperadmin(isSuperAdmin());
        setAuthed(true);
        setChecking(false);
        return;
      }

      // requireSession already triggered a redirect to the central login.
      if (bounces >= MAX_BOUNCES) {
        setAuthError(
          "Impossibile ripristinare la sessione. Ricarica la pagina o accedi di nuovo dal login centralizzato.",
        );
        setChecking(false);
        return;
      }
      try {
        sessionStorage.setItem(AUTH_BOUNCE_COUNT_KEY, String(bounces + 1));
      } catch {
        /* ignore */
      }
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const appUrls = useMemo(() => getAppUrls(), []);
  const email = currentUserEmail();
  const displayName = displayNameFromEmail(email);
  const initials = initialsFromEmail(email);

  const visibleApps = useMemo(
    () =>
      LAUNCHER_APPS.filter((a) => a.role === "all" || superadmin).filter(
        (a) => Boolean(appUrls[a.id]),
      ),
    [appUrls, superadmin],
  );

  async function onRefreshSession() {
    setRefreshing(true);
    try {
      await forceRefreshTokens();
      setSuperadmin(isSuperAdmin());
    } finally {
      setRefreshing(false);
    }
  }

  if (authError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface p-6 text-center">
        <p className="max-w-md text-sm text-red-600">{authError}</p>
        <div className="flex flex-wrap justify-center gap-2">
          <button
            type="button"
            className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
            onClick={() => {
              try {
                sessionStorage.removeItem(AUTH_BOUNCE_COUNT_KEY);
              } catch {
                /* ignore */
              }
              window.location.reload();
            }}
          >
            Ricarica
          </button>
          <button
            type="button"
            className="rounded-lg bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700"
            onClick={() => {
              try {
                sessionStorage.removeItem(AUTH_BOUNCE_COUNT_KEY);
              } catch {
                /* ignore */
              }
              redirectToLogin();
            }}
          >
            Vai al login
          </button>
        </div>
      </div>
    );
  }

  if (checking || !authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface" role="status">
        <p className="text-sm text-slate-500">
          {checking ? "Verifica sessione…" : "Reindirizzamento al login…"}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface">
      <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-6">
        <div className="flex items-center gap-2">
          <img src="/oceany.svg" alt="Oceany" className="h-7 w-7" />
          <span className="text-sm font-semibold text-slate-900">Oceany</span>
        </div>
        <UserMenu email={email} displayName={displayName} initials={initials} />
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-slate-900">
            Ciao {displayName}, apri un'app
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Le tue app della piattaforma Oceany. Accesso unico condiviso tra tutti i servizi.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleApps.map((app) => (
            <AppTile key={app.id} app={app} url={appUrls[app.id]!} />
          ))}
        </div>

        <div className="mt-10 flex items-center gap-3 text-xs text-slate-400">
          <span>Non vedi tutte le app?</span>
          <button
            type="button"
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            onClick={() => void onRefreshSession()}
            disabled={refreshing}
          >
            {refreshing ? "Aggiornamento…" : "Aggiorna sessione"}
          </button>
        </div>
      </main>
    </div>
  );
}

function AppTile({ app, url }: { app: LauncherApp; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 text-left transition hover:-translate-y-0.5 hover:border-ocean-200 hover:shadow-md"
    >
      <span
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl"
        style={{ backgroundColor: `${app.accent}1a` }}
        aria-hidden
      >
        {app.icon}
      </span>
      <span className="min-w-0">
        <span className="block text-base font-semibold text-slate-900 group-hover:text-ocean-700">
          {app.name}
        </span>
        <span className="mt-0.5 block text-sm text-slate-500">{app.description}</span>
      </span>
    </a>
  );
}

function UserMenu({
  email,
  displayName,
  initials,
}: {
  email: string;
  displayName: string;
  initials: string;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-slate-50"
        onClick={() => setMenuOpen((o) => !o)}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ocean-100 text-xs font-semibold text-ocean-700">
          {initials}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block text-sm font-medium text-slate-900">{displayName}</span>
          <span className="block text-[11px] text-slate-500">Oceany Team</span>
        </span>
      </button>
      {menuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            aria-label="Chiudi menu"
            onClick={() => setMenuOpen(false)}
          />
          <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
            <p className="truncate px-3 py-2 text-xs text-slate-500">{email}</p>
            <hr className="border-slate-100" />
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setMenuOpen(false);
                performLogout();
              }}
            >
              Esci
            </button>
          </div>
        </>
      )}
    </div>
  );
}
